import { QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ProvedorDeDashboard } from "./modules/dashboard/application/dashboard.contexto";
import { DashboardGatewayHttp } from "./modules/dashboard/infrastructure/dashboard.gateway";
import { PaginaDashboard } from "./modules/dashboard/presentation/pagina-dashboard";
import { ProvedorDeClientes } from "./modules/clientes/application/clientes.contexto";
import { ClientesGatewayHttp } from "./modules/clientes/infrastructure/clientes.gateway";
import { ModalNovoWorkspace } from "./modules/clientes/presentation/modal-novo-workspace";
import { ProvedorDeReferencias } from "./modules/referencias/application/referencias.contexto";
import { ReferenciasGatewayHttp } from "./modules/referencias/infrastructure/referencias.gateway";
import { PaginaDeConfiguracoes } from "./modules/referencias/presentation/pagina-de-configuracoes";
import { PaginaDeReferencias } from "./modules/referencias/presentation/pagina-de-referencias";
import { PaginaDeStories } from "./modules/referencias/presentation/pagina-de-stories";
import {
  rotaInicialPosLogin,
  useRotaDoApp,
} from "./modules/referencias/presentation/navegacao-do-app";
import { ProvedorDeMcp } from "./modules/mcp/application/mcp.contexto";
import { TokensPessoaisGatewayHttp } from "./modules/mcp/infrastructure/tokens-pessoais.gateway";
import { PaginaMcp } from "./modules/mcp/presentation/pagina-mcp";
import { PaginaConsentimentoOauth } from "./modules/mcp/presentation/pagina-consentimento-oauth";
import { ProvedorDeMidias } from "./modules/midias/application/midias.contexto";
import { MidiasGatewayHttp } from "./modules/midias/infrastructure/midias.gateway";
import { PaginaBiblioteca } from "./modules/midias/presentation/pagina-biblioteca";
import { ProvedorDeMonitoramentoGrupos } from "./modules/monitoramento-grupos/application/monitoramento-grupos.contexto";
import { MonitoramentoGruposGatewayHttp } from "./modules/monitoramento-grupos/infrastructure/monitoramento-grupos.gateway";
import { ProvedorDePublicacoes } from "./modules/publicacoes/application/publicacoes.contexto";
import { PublicacoesGatewayHttp } from "./modules/publicacoes/infrastructure/publicacoes.gateway";
import { PaginaAgenda } from "./modules/publicacoes/presentation/pagina-agenda";
import { PaginaFalhasPublicacao } from "./modules/publicacoes/presentation/pagina-falhas-publicacao";
import { PaginaPublicadosPublicacao } from "./modules/publicacoes/presentation/pagina-publicados-publicacao";
import { ProvedorDeUsuarios } from "./modules/usuarios/application/usuarios.contexto";
import { UsuariosGatewayHttp } from "./modules/usuarios/infrastructure/usuarios.gateway";
import { PaginaAdminUsuarios } from "./modules/usuarios/presentation/pagina-admin-usuarios";
import { AuthGatewayHttp } from "./shared/auth/auth.gateway";
import { ProvedorDeAuth, useAuth } from "./shared/auth/auth.contexto";
import { PaginaDeLogin } from "./shared/auth/pagina-de-login";
import { PaginaDePerfil } from "./shared/auth/pagina-de-perfil";
import { fonteDeTokenDoNavegador } from "./shared/auth/token";
import { ErroDaApi } from "./shared/http/erro-da-api";
import { LogoRenkeRefs } from "./shared/branding/logo-renke-refs";
import { ClienteHttp } from "./shared/http/cliente-http";
import { criarClienteDeConsulta } from "./shared/query/cliente-de-consulta";
import { PaginaDesignSystem } from "./pages/pagina-design-system";
import { ProvedorDeProjeto } from "./shared/tenant/projeto.contexto";
import { ProvedorDeDialogos } from "./shared/ui/dialogos.contexto";
import { aplicarTemaRenke, Button, temaRenkeSalvo } from "@base/ui/renke";
import { notificarOpenerEFecharSePopupOauth } from "./shared/auth/oauth-popup";

const oauthResolvidoNoPopup =
  typeof window !== "undefined" ? notificarOpenerEFecharSePopupOauth() : null;

/**
 * Creative OS: dashboard, referencias, clientes, agenda e admin.
 */
const tokens = fonteDeTokenDoNavegador();
const http = new ClienteHttp(
  `${import.meta.env.VITE_URL_DA_API ?? ""}/api`,
  tokens.tokenAtual,
);
const authGateway = new AuthGatewayHttp(http);
http.configurarRenovacaoDeToken(async () => {
  try {
    const { token } = await authGateway.refresh();
    tokens.definirToken(token, {
      manterConectado: tokens.manterConectadoAtual(),
    });
    return token;
  } catch (causa) {
    // Só sessão recusada (401) desloga; falha de rede mantém o token.
    if (causa instanceof ErroDaApi && causa.ehNaoAutorizado) {
      tokens.definirToken(null);
      window.location.reload();
    }
    return null;
  }
});
const referenciasGateway = new ReferenciasGatewayHttp(http);
const clientesGateway = new ClientesGatewayHttp(http);
const dashboardGateway = new DashboardGatewayHttp(http);
const usuariosGateway = new UsuariosGatewayHttp(http);
const publicacoesGateway = new PublicacoesGatewayHttp(http);
const midiasGateway = new MidiasGatewayHttp(http);
const monitoramentoGruposGateway = new MonitoramentoGruposGatewayHttp(http);
const tokensPessoaisGateway = new TokensPessoaisGatewayHttp(http);
const clienteDeConsulta = criarClienteDeConsulta();

function ehRotaDesignSystem(hash = window.location.hash): boolean {
  const limpo = hash.replace(/^#\/?/, "");
  return limpo === "design-system" || limpo.startsWith("design-system/");
}

function Rotas() {
  const rota = useRotaDoApp();
  if (rota === "perfil") return <PaginaDePerfil />;
  if (rota === "configuracoes" || rota === "workspace") {
    return <PaginaDeConfiguracoes />;
  }
  if (rota === "admin-usuarios") return <PaginaAdminUsuarios />;
  if (rota === "dashboard") return <PaginaDashboard />;
  if (rota === "agenda") return <PaginaAgenda />;
  if (rota === "falhas-publicacao") return <PaginaFalhasPublicacao />;
  if (rota === "publicados-publicacao") return <PaginaPublicadosPublicacao />;
  if (rota === "biblioteca") return <PaginaBiblioteca />;
  if (rota === "stories") return <PaginaDeStories />;
  if (rota === "mcp") return <PaginaMcp />;
  if (rota === "oauth-consent") return <PaginaConsentimentoOauth />;
  return <PaginaDeReferencias />;
}

function GuardaDeAuth() {
  const { sessao, carregando, semConexao, erro, tentarDeNovo } = useAuth();

  useEffect(() => {
    aplicarTemaRenke(temaRenkeSalvo());
  }, []);

  useEffect(() => {
    if (!sessao || carregando) return;
    const limpo = window.location.hash.replace(/^#/, "").split("?")[0] ?? "";
    if (!limpo) {
      window.location.hash = rotaInicialPosLogin(sessao.escopos);
    }
  }, [sessao, carregando]);

  if (carregando) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-page-background">
        <p className="text-sm text-muted-foreground" role="status">
          <LogoRenkeRefs className="mx-auto mb-4 h-10" />
          Validando acesso…
        </p>
      </div>
    );
  }

  if (!sessao && semConexao) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background px-6">
        <div className="w-full max-w-sm rounded-2xl border border-border-subtle bg-card p-6 text-center">
          <LogoRenkeRefs className="mx-auto mb-5 h-9" />
          <h1 className="text-base font-semibold text-foreground">
            Sem conexão com o servidor
          </h1>
          <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
            Sua sessão continua salva. Assim que o servidor responder, você
            entra sem digitar a senha de novo.
          </p>
          {erro ? (
            <p className="mt-3 text-xs text-muted-foreground/80">{erro}</p>
          ) : null}
          <Button
            variante="brand"
            tamanho="md"
            className="mt-5 w-full"
            onClick={() => void tentarDeNovo()}
          >
            Tentar de novo
          </Button>
        </div>
      </div>
    );
  }

  if (!sessao) return <PaginaDeLogin />;

  return (
    <ProvedorDeReferencias gateway={referenciasGateway}>
      <ProvedorDeClientes gateway={clientesGateway}>
        <ProvedorDeDashboard gateway={dashboardGateway}>
          <ProvedorDeUsuarios gateway={usuariosGateway}>
            <ProvedorDePublicacoes gateway={publicacoesGateway}>
              <ProvedorDeMidias gateway={midiasGateway}>
                <ProvedorDeMonitoramentoGrupos
                  gateway={monitoramentoGruposGateway}
                >
                  <ProvedorDeMcp gateway={tokensPessoaisGateway}>
                    <ProvedorDeProjeto>
                      <ModalNovoWorkspace />
                      <Rotas />
                    </ProvedorDeProjeto>
                  </ProvedorDeMcp>
                </ProvedorDeMonitoramentoGrupos>
              </ProvedorDeMidias>
            </ProvedorDePublicacoes>
          </ProvedorDeUsuarios>
        </ProvedorDeDashboard>
      </ProvedorDeClientes>
    </ProvedorDeReferencias>
  );
}

export function App() {
  const [designSystem, definirDesignSystem] = useState(() =>
    ehRotaDesignSystem(),
  );

  useEffect(() => {
    const aoMudar = () => definirDesignSystem(ehRotaDesignSystem());
    window.addEventListener("hashchange", aoMudar);
    return () => window.removeEventListener("hashchange", aoMudar);
  }, []);

  if (oauthResolvidoNoPopup) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-page-background px-6 text-center">
        <LogoRenkeRefs className="h-10" />
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">
            {oauthResolvidoNoPopup.endsWith("_ok")
              ? "Conexão concluída"
              : "Não foi possível conectar"}
          </p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Esta janela pode ser fechada. Se não fechar sozinha, use o botão
            abaixo.
          </p>
        </div>
        <Button
          type="button"
          variante="secondary"
          tamanho="sm"
          onClick={() => window.close()}
        >
          Fechar
        </Button>
      </div>
    );
  }

  if (designSystem) {
    return <PaginaDesignSystem />;
  }

  return (
    <QueryClientProvider client={clienteDeConsulta}>
      <ProvedorDeAuth gateway={authGateway} tokens={tokens}>
        <ProvedorDeDialogos>
          <GuardaDeAuth />
        </ProvedorDeDialogos>
      </ProvedorDeAuth>
    </QueryClientProvider>
  );
}
